import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import Panel from 'components/panels/Panel/Panel';
import useAuth from 'hooks/useAuth';
import i18n from 'i18next';
import MaintenanceIcon from './maintenance.svg';

const getErrorMsg = (routeError) => {
  if (isRouteErrorResponse(routeError)) {
    if (routeError.status === 404) {
      return `This page doesn't exist!`;
    }

    if (routeError.status === 401) {
      return `You aren't authorized to see this`;
    }

    if (routeError.status === 503) {
      return 'Looks like our API is down';
    }
  }
  return i18n.t('components:errorFallback.title');
};

const ErrorFallback = ({
  error,
  resetError,
}: {
  error?: Sentry.ReportDialogOptions | undefined;
  resetError?: () => void;
}) => {
  const routeError = useRouteError(); // This captures the route-specific error
  const { logout } = useAuth();
  const { palette } = useTheme();

  const handleReset = () => {
    if (resetError) {
      resetError(); // Sentry specific reset
    } else {
      window.location.reload(); // Route error, reload the page
    }
  };

  return (
    <Panel>
      <Grid container spacing={3} direction="column" alignItems="center">
        <Grid item>
          <Typography variant="h1">{getErrorMsg(routeError)}</Typography>
        </Grid>
        <Grid item>
          <img
            src={MaintenanceIcon}
            alt="Maintenance"
            style={palette.mode === 'dark' ? { filter: 'invert(1) hue-rotate(180deg)' } : undefined}
          />
        </Grid>
        <Grid item container direction="row" justifyContent="center" spacing={2}>
          <Grid item>
            <Button variant="contained" onClick={() => handleReset()} data-testid="reset-button">
              {i18n.t('components:errorFallback.resetButton')}
            </Button>
          </Grid>
          {!!error && (
            <Grid item>
              <Button variant="outlined" onClick={() => Sentry.showReportDialog(error)}>
                {i18n.t('components:errorFallback.reportButton')}
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button variant="outlined" onClick={() => logout()}>
              Logout
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Panel>
  );
};

export default ErrorFallback;
